import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  Tooltip,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  alpha,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  Refresh,
  Visibility,
  VisibilityOff,
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  OpenInNew,
} from '@mui/icons-material';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface App {
  id: string;
  name: string;
  description: string;
  api_key: string;
  webhook_url: string;
  webhook_secret: string;
  status: string;
  total_charges: number;
  total_paid: number;
  total_amount: string;
  last_activity_at: string;
  created_at: string;
  charges_count: string;
  paid_count: string;
}

interface AppCharge {
  id: string;
  txid: string;
  external_id: string;
  valor: string;
  descricao: string;
  status: string;
  paid_at: string;
  paid_amount: string;
  end_to_end_id: string;
  payer_info: string;
  webhook_sent: boolean;
  webhook_attempts: number;
  created_at: string;
}

interface WebhookLog {
  id: string;
  event_type: string;
  url: string;
  response_status: number;
  response_time_ms: number;
  success: boolean;
  error_message: string;
  attempt: number;
  created_at: string;
}

const AppsPage: React.FC = () => {
  const theme = useTheme();
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [detailApp, setDetailApp] = useState<App | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [charges, setCharges] = useState<AppCharge[]>([]);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});

  // Form
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formWebhookUrl, setFormWebhookUrl] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    setLoading(true);
    try {
      const response = await api.get('/apps');
      if (response?.success) {
        setApps(response.data || []);
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao carregar apps');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingApp(null);
    setFormName('');
    setFormDescription('');
    setFormWebhookUrl('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (app: App) => {
    setEditingApp(app);
    setFormName(app.name);
    setFormDescription(app.description || '');
    setFormWebhookUrl(app.webhook_url);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formWebhookUrl.trim()) {
      toast.error('Nome e URL de webhook são obrigatórios');
      return;
    }

    setFormSaving(true);
    try {
      const data = {
        name: formName,
        description: formDescription,
        webhook_url: formWebhookUrl,
      };

      if (editingApp) {
        await api.put(`/apps/${editingApp.id}`, data);
        toast.success('App atualizado!');
      } else {
        await api.post('/apps', data);
        toast.success('App criado com sucesso!');
      }

      setDialogOpen(false);
      loadApps();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async (app: App) => {
    if (!confirm(`Tem certeza que deseja excluir "${app.name}"? Todas as cobranças vinculadas serão perdidas.`)) return;

    try {
      await api.delete(`/apps/${app.id}`);
      toast.success('App excluído');
      loadApps();
      if (detailApp?.id === app.id) setDetailApp(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao excluir');
    }
  };

  const handleRegenerateKey = async (app: App) => {
    if (!confirm(`Regenerar a API Key de "${app.name}"? A chave anterior deixará de funcionar imediatamente.`)) return;

    try {
      await api.post(`/apps/${app.id}/regenerate-key`);
      toast.success('API Key regenerada!');
      loadApps();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao regenerar');
    }
  };

  const handleToggleStatus = async (app: App) => {
    try {
      const newStatus = app.status === 'active' ? 'inactive' : 'active';
      await api.put(`/apps/${app.id}`, { status: newStatus });
      toast.success(`App ${newStatus === 'active' ? 'ativado' : 'desativado'}`);
      loadApps();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao alterar status');
    }
  };

  const handleViewDetails = async (app: App) => {
    setDetailApp(app);
    setDetailTab(0);
    setLoadingDetail(true);
    try {
      const [chargesRes, logsRes] = await Promise.all([
        api.get(`/apps/${app.id}/charges`),
        api.get(`/apps/${app.id}/webhook-logs`),
      ]);
      setCharges(chargesRes?.data || []);
      setWebhookLogs(logsRes?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR');
  };

  const apiBaseUrl = window.location.hostname.includes('localhost')
    ? 'http://localhost:3333/api/v1'
    : 'https://api.pay.viptecnologia.com.br/api/v1';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Apps Integrados
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadApps}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Novo App
          </Button>
        </Stack>
      </Box>

      {/* Lista de Apps */}
      <Paper sx={{ mb: 3 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : apps.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Nenhum app cadastrado.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cadastre um app para que sistemas externos possam gerar cobranças PIX e receber notificações de pagamento.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>App</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell align="center">Cobranças</TableCell>
                  <TableCell align="center">Pagas</TableCell>
                  <TableCell>Última Atividade</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.map((app) => (
                  <TableRow
                    key={app.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(app)}
                  >
                    <TableCell>
                      <Typography fontWeight="bold">{app.name}</Typography>
                      {app.description && (
                        <Typography variant="caption" color="text.secondary">
                          {app.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={app.status}
                        size="small"
                        color={app.status === 'active' ? 'success' : 'default'}
                        onClick={(e) => { e.stopPropagation(); handleToggleStatus(app); }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" fontFamily="monospace">
                          {showKeys[app.id] ? app.api_key : `${app.api_key.substring(0, 12)}...`}
                        </Typography>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowKeys(s => ({ ...s, [app.id]: !s[app.id] })); }}>
                          {showKeys[app.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(app.api_key, 'API Key'); }}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">{app.charges_count || 0}</TableCell>
                    <TableCell align="center">{app.paid_count || 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {app.last_activity_at ? formatDate(app.last_activity_at) : 'Nunca'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(app); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Regenerar API Key">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRegenerateKey(app); }}>
                            <Refresh fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(app); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Detalhe do App selecionado */}
      {detailApp && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">{detailApp.name}</Typography>
            <IconButton onClick={() => setDetailApp(null)} size="small">&times;</IconButton>
          </Box>

          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
            <Tab label="Dados de Integração" />
            <Tab label={`Cobranças (${charges.length})`} />
            <Tab label={`Webhook Logs (${webhookLogs.length})`} />
          </Tabs>

          {detailTab === 0 && (
            <Box>
              {/* Dados de conexão */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Dados de Integração para o App</AlertTitle>
                Envie essas informações para a equipe de desenvolvimento do app integrado.
              </Alert>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">URL Base da API</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontFamily="monospace" sx={{ p: 1, backgroundColor: alpha(theme.palette.grey[500], 0.08), borderRadius: 1, flex: 1 }}>
                      {apiBaseUrl}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(apiBaseUrl, 'URL')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">API Key (header X-API-Key)</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontFamily="monospace" sx={{ p: 1, backgroundColor: alpha(theme.palette.grey[500], 0.08), borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                      {detailApp.api_key}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(detailApp.api_key, 'API Key')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Webhook Secret (para verificar assinatura)</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontFamily="monospace" sx={{ p: 1, backgroundColor: alpha(theme.palette.grey[500], 0.08), borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>
                      {detailApp.webhook_secret}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(detailApp.webhook_secret, 'Webhook Secret')}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">URL de Callback (onde enviamos notificações)</Typography>
                  <Typography fontFamily="monospace" sx={{ p: 1, backgroundColor: alpha(theme.palette.grey[500], 0.08), borderRadius: 1 }}>
                    {detailApp.webhook_url}
                  </Typography>
                </Box>

                <Divider />

                {/* Documentação inline */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight="bold">Documentação dos Endpoints</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={3}>
                      {/* Criar cobrança */}
                      <Box>
                        <Chip label="POST" color="success" size="small" sx={{ mr: 1 }} />
                        <Typography component="span" fontFamily="monospace" fontWeight="bold">
                          /external/pix/charge
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Cria uma cobrança PIX e retorna o QR Code para pagamento.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                          <Typography component="pre" fontFamily="monospace" fontSize="0.8rem" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
{`curl -X POST ${apiBaseUrl}/external/pix/charge \\
  -H "X-API-Key: ${detailApp.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "valor": "25.50",
    "descricao": "Pedido #1234",
    "expiracao": 3600,
    "external_id": "pedido-1234"
  }'`}
                          </Typography>
                        </Paper>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Parâmetros:</strong>
                        </Typography>
                        <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                          <li><code>valor</code> (obrigatório) - Valor em reais, string com 2 casas: "25.50"</li>
                          <li><code>descricao</code> - Texto exibido ao pagador</li>
                          <li><code>expiracao</code> - Tempo em segundos (padrão: 86400 = 24h)</li>
                          <li><code>external_id</code> - ID de referência do seu sistema</li>
                          <li><code>metadata</code> - Objeto JSON com dados extras</li>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Resposta:</strong> <code>txid</code>, <code>pixCopiaECola</code>, <code>qrCodeBase64</code>, <code>status</code>
                        </Typography>
                      </Box>

                      <Divider />

                      {/* Consultar cobrança */}
                      <Box>
                        <Chip label="GET" color="primary" size="small" sx={{ mr: 1 }} />
                        <Typography component="span" fontFamily="monospace" fontWeight="bold">
                          /external/pix/charge/:txid
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Consulta o status de uma cobrança. Retorna se foi paga com detalhes do pagamento.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                          <Typography component="pre" fontFamily="monospace" fontSize="0.8rem" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
{`curl ${apiBaseUrl}/external/pix/charge/SEU_TXID \\
  -H "X-API-Key: ${detailApp.api_key}"`}
                          </Typography>
                        </Paper>
                      </Box>

                      <Divider />

                      {/* Listar cobranças */}
                      <Box>
                        <Chip label="GET" color="primary" size="small" sx={{ mr: 1 }} />
                        <Typography component="span" fontFamily="monospace" fontWeight="bold">
                          /external/pix/charges
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Lista todas as cobranças do app com paginação.
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                          <Typography component="pre" fontFamily="monospace" fontSize="0.8rem" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
{`curl "${apiBaseUrl}/external/pix/charges?status=ATIVA&page=1&limit=20" \\
  -H "X-API-Key: ${detailApp.api_key}"`}
                          </Typography>
                        </Paper>
                      </Box>

                      <Divider />

                      {/* Webhook callback */}
                      <Box>
                        <Typography fontWeight="bold" sx={{ mb: 1 }}>Webhook de Pagamento (callback)</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Quando um pagamento for confirmado, enviaremos um POST para <code>{detailApp.webhook_url}</code> com o seguinte payload:
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
                          <Typography component="pre" fontFamily="monospace" fontSize="0.8rem" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
{`{
  "event": "payment_received",
  "timestamp": "2026-03-16T14:30:00.000Z",
  "data": {
    "txid": "abc123...",
    "external_id": "pedido-1234",
    "valor": "25.50",
    "descricao": "Pedido #1234",
    "pagamento": {
      "valor": "25.50",
      "horario": "2026-03-16T14:29:55.000Z",
      "endToEndId": "E00311312026...",
      "pagador": { "nome": "João Silva" }
    }
  }
}`}
                          </Typography>
                        </Paper>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Headers enviados:</strong>
                        </Typography>
                        <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
                          <li><code>X-Webhook-Signature</code> - HMAC-SHA256 do body usando o Webhook Secret</li>
                          <li><code>X-Webhook-Event</code> - Tipo do evento (payment_received)</li>
                          <li><code>X-Webhook-Timestamp</code> - Timestamp do envio</li>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Retorne status <strong>2xx</strong> para confirmar o recebimento. Caso contrário, faremos até 3 tentativas.
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Box>
          )}

          {detailTab === 1 && (
            <Box>
              {loadingDetail ? (
                <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
              ) : charges.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                  Nenhuma cobrança gerada por este app.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>TXID</TableCell>
                        <TableCell>External ID</TableCell>
                        <TableCell>Valor</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Webhook</TableCell>
                        <TableCell>Criado em</TableCell>
                        <TableCell>Pago em</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {charges.map((charge) => (
                        <TableRow key={charge.id}>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {charge.txid.substring(0, 20)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{charge.external_id || '-'}</Typography>
                          </TableCell>
                          <TableCell>R$ {parseFloat(charge.valor).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={charge.status === 'CONCLUIDA' ? 'PAGO' : charge.status}
                              size="small"
                              color={charge.status === 'CONCLUIDA' ? 'success' : charge.status === 'ATIVA' ? 'info' : 'default'}
                              icon={charge.status === 'CONCLUIDA' ? <CheckCircle /> : undefined}
                            />
                          </TableCell>
                          <TableCell>
                            {charge.status === 'CONCLUIDA' && (
                              <Chip
                                label={charge.webhook_sent ? 'Enviado' : `Tentativa ${charge.webhook_attempts || 0}`}
                                size="small"
                                color={charge.webhook_sent ? 'success' : 'warning'}
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(charge.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{charge.paid_at ? formatDate(charge.paid_at) : '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {detailTab === 2 && (
            <Box>
              {loadingDetail ? (
                <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
              ) : webhookLogs.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                  Nenhum webhook enviado para este app.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Evento</TableCell>
                        <TableCell>URL</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tempo</TableCell>
                        <TableCell>Tentativa</TableCell>
                        <TableCell>Resultado</TableCell>
                        <TableCell>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {webhookLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip label={log.event_type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {log.url}
                            </Typography>
                          </TableCell>
                          <TableCell>{log.response_status || '-'}</TableCell>
                          <TableCell>{log.response_time_ms ? `${log.response_time_ms}ms` : '-'}</TableCell>
                          <TableCell>{log.attempt}</TableCell>
                          <TableCell>
                            {log.success ? (
                              <CheckCircle color="success" fontSize="small" />
                            ) : (
                              <Tooltip title={log.error_message || 'Falhou'}>
                                <ErrorIcon color="error" fontSize="small" />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDate(log.created_at)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingApp ? 'Editar App' : 'Novo App'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nome do App *"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ex: Agente WhatsApp, E-commerce, PDV"
            />
            <TextField
              fullWidth
              label="Descrição"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              multiline
              rows={2}
              placeholder="Descrição do app e para que ele usa a integração"
            />
            <TextField
              fullWidth
              label="URL de Webhook (callback) *"
              value={formWebhookUrl}
              onChange={(e) => setFormWebhookUrl(e.target.value)}
              placeholder="https://seu-sistema.com/webhook/pagamento"
              helperText="Quando um pagamento for confirmado, enviaremos uma notificação POST para esta URL"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={formSaving}>
            {formSaving ? <CircularProgress size={20} /> : editingApp ? 'Salvar' : 'Criar App'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppsPage;
